import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import moment from "moment";

import LanguageContext from "@/context/language/LanguageContext";

import { useBaseDayPicker } from "../../shared/hooks/useBaseDayPicker";
import { SingleDayPickerProps } from "../types";

export const useSingleDayPicker = ({
  minDate,
  maxDate,
  disabled,
  externalValue,
  timeInput,
  selectedTime,
  onChange,
  onAfterClear
}: Pick<
  SingleDayPickerProps,
  | "minDate"
  | "maxDate"
  | "disabled"
  | "externalValue"
  | "onChange"
  | "timeInput"
  | "selectedTime"
  | "onAfterClear"
>) => {
  const { currentDateFormat } = useContext(LanguageContext);
  const { t } = useTranslation();

  const base = useBaseDayPicker({ minDate, maxDate, disabled, timeInput });

  const getInitialSelectedDay = (
    date: Date | null | undefined
  ): number | null => {
    if (!date) return null;
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    ).getTime();
  };

  const [selectedDay, setSelectedDay] = useState<number | null>(
    getInitialSelectedDay(externalValue)
  );

  const noSelectedDay = selectedDay === null;

  const openPicker = () => {
    base.openPicker(() => {
      if (noSelectedDay) {
        base.setYear(base.currentYear);
        base.setMonth(base.currentMonth);
      }
    });
  };

  const handleDayClick = (clickedKey: number) => {
    setSelectedDay(clickedKey);
    onChange?.(moment(clickedKey).toDate());
    base.closePicker();
  };

  const handleClear = (event: React.MouseEvent) => {
    if (disabled) return;
    event.stopPropagation();
    base.cancelHoverClear();
    onChange?.(null);
    setSelectedDay(null);
    onAfterClear?.();
  };

  const getDisplayValue = (): string => {
    if (noSelectedDay) {
      return timeInput
        ? t("Filter##select date and time")
        : t("Filter##select date");
    }
    const date = moment(selectedDay).format(currentDateFormat);
    return selectedTime ? `${date} ${selectedTime}` : date;
  };

  return {
    ...base,
    selectedDay,
    noSelectedDay,
    hasClearableValue: selectedDay !== null,
    openPicker,
    handleDayClick,
    handleClear,
    getDisplayValue
  };
};
